import { useEffect, useRef, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { Importacion, LineaImportacion, PruebaPermiso, ResultadoLinea } from '../../../api/ops/clientesImportar';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Ayuda } from '../../../componentes/Ayuda';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Fecha } from '../../../componentes/Fecha';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { OpcionRadio } from '../../../componentes/OpcionRadio';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Pasos } from '../../../componentes/Pasos';
import { Ventana } from '../../../componentes/Ventana';
import { cifra, diaMes, diaYHora, hoyLocal, kilobytes } from '../../../logica/fechasCortas';
import { textoDeError } from '../../equipo/textos';
import { AYUDA, ESTADO, FILTROS_LINEAS, LINEA_POR_DEFECTO, N4, NOTA_ERRORES_HECHA, PASOS, TEXTOS, totalImportable } from './textos';

export type AperturaImportar = { tipo: 'nueva' } | { tipo: 'ver'; importacion: Importacion };

type Fase = 'editar' | 'subiendo' | 'comprobando' | 'revisar' | 'importando' | 'hecha' | 'no-valido' | 'descartada' | 'fallida';

const MAX_BYTES = 20 * 1024 * 1024;
/** Cada cuánto se mira cómo va una importación que se está comprobando o importando. */
export const ESPERA_SONDEO_MS = 1500;

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    apertura: AperturaImportar | null;
    puedeImportar: boolean;
    onPlantilla: () => void;
    onCerrar: () => void;
    /** Algo ha cambiado en la lista (subida, confirmada…). */
    onCambio: () => void;
    onDescartada: () => void;
    onIr: (ruta: string) => void;
}

function faseDe(i: Importacion): Fase {
    switch (i.status) {
        case 'validating':
            return 'comprobando';
        case 'invalid':
            return 'no-valido';
        case 'preview_ready':
            return 'revisar';
        case 'importing':
            return 'importando';
        case 'completed':
            return 'hecha';
        case 'failed':
            return 'fallida';
        case 'cancelled':
            return 'descartada';
    }
}

const RESUMEN_DE: Record<ResultadoLinea, 'new' | 'updated' | 'merged' | 'doubtful' | 'errors' | 'suppressed'> = { new: 'new', update: 'updated', merged: 'merged', doubtful: 'doubtful', error: 'errors', suppressed: 'suppressed' };

/** Lo que se ve de una línea: el error con su valor (G149), nunca la línea entera. */
function textoDeLinea(l: LineaImportacion): { campo: string; texto: string } {
    const primero = l.errors?.[0];
    if (primero === undefined) {
        return { campo: '', texto: LINEA_POR_DEFECTO[l.outcome] ?? '' };
    }
    const valor = primero.value ?? null;
    const mensaje = primero.message ?? '';
    const texto = valor === null || mensaje.includes(valor) ? mensaje : `«${valor}»: ${mensaje}`;
    return { campo: primero.field, texto: texto === '' ? (LINEA_POR_DEFECTO[l.outcome] ?? '') : texto };
}

function terminoEl(iso: string | null | undefined): string {
    if (iso == null) {
        return '';
    }
    const f = new Date(iso);
    const hora = `${String(f.getHours()).padStart(2, '0')}:${String(f.getMinutes()).padStart(2, '0')}`;
    return diaYHora(iso).startsWith('Hoy') ? `Terminó hoy a las ${hora}.` : `Terminó el ${diaMes(iso)} a las ${hora}.`;
}

/**
 * Panel de 2.6 (RV Panel de ficha): una importación nueva con RV Pasos (Archivo · Permisos · Revisar) o una ya hecha. La
 * subida declara la prueba del permiso comercial con su origen y su fecha (N4, G202); «Cambiar» los permisos en Revisar
 * vuelve al paso Permisos y, al seguir, descarta esta vista previa y sube el archivo otra vez (G149). Mientras se comprueba
 * o se importa, se mira cómo va cada poco. Descartar va con ventana, sin «Deshacer» (G159).
 */
export function PanelImportar({ ops, negocio, movil, apertura, puedeImportar, onPlantilla, onCerrar, onCambio, onDescartada, onIr }: Props) {
    const avisar = useAvisoBreve();
    const entrada = useRef<HTMLInputElement>(null);
    const [vista, setVista] = useState<AperturaImportar | null>(null);
    const [importacion, setImportacion] = useState<Importacion | null>(null);
    const [paso, setPaso] = useState(0);
    const [subiendo, setSubiendo] = useState(false);
    const [cambiando, setCambiando] = useState(false);
    const [archivo, setArchivo] = useState<File | null>(null);
    const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
    const [arrastrando, setArrastrando] = useState(false);
    const [prueba, setPrueba] = useState<PruebaPermiso>('none');
    const [origen, setOrigen] = useState('');
    const [fecha, setFecha] = useState('');
    const [filtro, setFiltro] = useState<ResultadoLinea>('error');
    const [lineas, setLineas] = useState<{ clave: string; lineas: LineaImportacion[] } | null>(null);
    const [descartar, setDescartar] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    if (apertura !== vista) {
        setVista(apertura);
        setImportacion(apertura?.tipo === 'ver' ? apertura.importacion : null);
        setPaso(0);
        setSubiendo(false);
        setCambiando(false);
        setArchivo(null);
        setErrorArchivo(null);
        setPrueba('none');
        setOrigen('');
        setFecha('');
        setFiltro('error');
        setLineas(null);
        setDescartar(false);
    }

    const nueva = apertura?.tipo === 'nueva';
    const fase: Fase = importacion === null || cambiando ? (subiendo ? 'subiendo' : 'editar') : faseDe(importacion);
    const id = importacion?.id ?? null;
    const estado = importacion?.status ?? null;

    // Mientras se comprueba o se importa, se mira cómo va.
    useEffect(() => {
        if (id === null || (estado !== 'validating' && estado !== 'importing')) {
            return;
        }
        let vivo = true;
        const reloj = setTimeout(() => {
            ops.getCustomerImport(negocio, id)
                .then((i) => {
                    if (vivo) {
                        setImportacion(i);
                        if (i.status !== 'validating' && i.status !== 'importing') {
                            onCambio();
                        }
                    }
                })
                .catch(() => {
                    // Se vuelve a mirar en la siguiente vuelta.
                    if (vivo) {
                        setImportacion((x) => (x === null ? x : { ...x }));
                    }
                });
        }, ESPERA_SONDEO_MS);
        return () => {
            vivo = false;
            clearTimeout(reloj);
        };
    }, [ops, negocio, id, estado, importacion, onCambio]);

    // Las líneas del filtro elegido.
    const conLineas = !cambiando && (estado === 'preview_ready' || estado === 'completed' || estado === 'invalid');
    const claveLineas = `${id ?? ''}|${filtro}|${estado ?? ''}`;
    useEffect(() => {
        if (id === null || !conLineas) {
            return;
        }
        let vivo = true;
        ops.getCustomerImport(negocio, id, estado === 'invalid' ? undefined : filtro)
            .then((i) => {
                if (vivo) {
                    setLineas({ clave: claveLineas, lineas: (i.lines ?? []).filter((l) => estado === 'invalid' || l.outcome === filtro) });
                }
            })
            .catch(() => {
                if (vivo) {
                    setLineas({ clave: claveLineas, lineas: [] });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, filtro, estado, conLineas, claveLineas]);

    const elegirArchivo = (f: File | undefined) => {
        if (f === undefined) {
            return;
        }
        if (!/\.csv$/i.test(f.name)) {
            setErrorArchivo(TEXTOS.noCsv);
            return;
        }
        if (f.size > MAX_BYTES) {
            setErrorArchivo(TEXTOS.grande);
            return;
        }
        setArchivo(f);
        setErrorArchivo(null);
    };

    const subir = () => {
        if (archivo === null) {
            setPaso(0);
            setErrorArchivo(TEXTOS.faltaArchivo);
            return;
        }
        setSubiendo(true);
        // «Cambiar» los permisos: la vista previa anterior se descarta y se crea una importación nueva (G149).
        const anterior = cambiando && importacion !== null && importacion.status === 'preview_ready' ? ops.cancelCustomerImport(negocio, importacion.id).catch(() => undefined) : Promise.resolve(undefined);
        anterior
            .then(() => ops.createCustomerImport(negocio, { archivo, nombre: archivo.name, prueba, origen: origen.trim(), fecha }))
            .then((i) => {
                setCambiando(false);
                setImportacion(i);
                setPaso(2);
                setFiltro('error');
                onCambio();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido subir el archivo. Prueba de nuevo.') });
            })
            .finally(() => {
                setSubiendo(false);
            });
    };

    const confirmar = () => {
        if (importacion === null) {
            return;
        }
        setOcupado(true);
        ops.confirmCustomerImport(negocio, importacion.id)
            .then((i) => {
                setImportacion(i);
                onCambio();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido importar. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const hacerDescartar = () => {
        if (importacion === null) {
            return;
        }
        setOcupado(true);
        ops.cancelCustomerImport(negocio, importacion.id)
            .then(() => {
                setDescartar(false);
                onDescartada();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido descartar la vista previa. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const resumen = importacion?.summary ?? {};
    const total = importacion === null ? 0 : totalImportable(importacion);
    const migracion = importacion?.source === 'migration_v1';
    const declarada = (importacion?.consent_evidence ?? prueba) === 'declared';
    const filtros = FILTROS_LINEAS.filter((f) => f.resultado === 'error' || (resumen[RESUMEN_DE[f.resultado]] ?? 0) > 0);
    const filtroActual = filtros.find((f) => f.resultado === filtro) ?? filtros[0];
    const lineasVistas = lineas?.clave === claveLineas ? lineas.lineas : null;
    const enPasos = nueva && (fase === 'editar' || fase === 'subiendo' || fase === 'comprobando' || (fase === 'revisar' && puedeImportar));

    const titulo = nueva ? 'Importar clientes' : migracion ? 'Migración desde la v1' : (importacion?.file_name ?? 'Importación');
    const subtitulo = nueva
        ? archivo !== null
            ? `${archivo.name} · ${kilobytes(archivo.size)}`
            : 'CSV con la plantilla de Reservaria, hasta 20 MB'
        : importacion === null
          ? ''
          : `${diaYHora(importacion.created_at)} · ${ESTADO[importacion.status][0]}`;
    const ayuda = !nueva && fase === 'hecha' ? AYUDA.ver : fase === 'editar' || fase === 'subiendo' ? (paso === 0 ? AYUDA.archivo : AYUDA.permisos) : AYUDA.revisar;

    const pasoArchivo = (
        <div className="rv-ca-cuerpo rv-imp-paso">
            <div className="rv-imp-plantilla">
                <span className="rv-imp-plantilla__baldosa">
                    <Icono nombre="file-spreadsheet" />
                </span>
                <span className="rv-imp-plantilla__texto">
                    <strong>{TEXTOS.plantillaTitulo}</strong>
                    <span>{TEXTOS.plantillaTexto}</span>
                </span>
                <Boton tipo="secundario" icono="download" onClick={onPlantilla}>
                    Descargar plantilla
                </Boton>
            </div>
            {archivo === null ? (
                <div
                    className={`rv-imp-soltar${arrastrando ? ' rv-imp-soltar--encima' : ''}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setArrastrando(true);
                    }}
                    onDragLeave={() => {
                        setArrastrando(false);
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        setArrastrando(false);
                        elegirArchivo(e.dataTransfer.files[0]);
                    }}
                >
                    <span className="rv-imp-soltar__baldosa">
                        <Icono nombre="upload" />
                    </span>
                    <strong>{TEXTOS.elegir}</strong>
                    <span>{movil ? TEXTOS.elegirAyudaMovil : TEXTOS.elegirAyuda}</span>
                    <Boton tipo="secundario" icono="file-spreadsheet" movil={movil} onClick={() => entrada.current?.click()}>
                        Elegir archivo
                    </Boton>
                </div>
            ) : (
                <div className="rv-imp-elegido">
                    <span className="rv-ca-baldosa">
                        <Icono nombre="file-spreadsheet" />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>{archivo.name}</strong>
                        <span>{`${kilobytes(archivo.size)} · listo para revisar`}</span>
                    </span>
                    <Boton tipo="secundario" icono="refresh-cw" onClick={() => entrada.current?.click()}>
                        Cambiar archivo
                    </Boton>
                </div>
            )}
            <input
                ref={entrada}
                type="file"
                accept=".csv,text/csv"
                className="rv-sr"
                aria-label="Archivo CSV"
                tabIndex={-1}
                onChange={(e) => {
                    elegirArchivo(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            {errorArchivo !== null && (
                <span role="alert" className="rv-campo__error">
                    <Icono nombre="circle-alert" />
                    <span>{errorArchivo}</span>
                </span>
            )}
        </div>
    );

    const pasoPermisos = (
        <div className="rv-ca-cuerpo rv-imp-paso">
            <div className="rv-ca-bloque__cab">
                <strong className="rv-imp-pregunta">{N4.pregunta}</strong>
                <span>{N4.ejemplo}</span>
            </div>
            <div role="radiogroup" aria-label="Prueba del permiso comercial" className="rv-imp-opciones">
                <OpcionRadio elegida={prueba === 'declared'} titulo={N4.si} texto={N4.siTexto} onElegir={() => { setPrueba('declared'); }} />
                {prueba === 'declared' && (
                    <div className="rv-imp-origen">
                        <div className="rv-ca-bloque__cab">
                            <strong>{N4.origen}</strong>
                            <span>{N4.origenEjemplo}</span>
                        </div>
                        <Campo etiqueta="De dónde viene" valor={origen} onChange={setOrigen} max={120} nombre="origen-permiso" />
                        <Fecha etiqueta="Cuándo" valor={fecha} onChange={setFecha} max={hoyLocal()} nombre="fecha-permiso" />
                        <p className="rv-ca-nota">
                            <Icono nombre="shield-check" />
                            <span>{N4.responsable}</span>
                        </p>
                    </div>
                )}
                <OpcionRadio elegida={prueba === 'none'} titulo={N4.no} texto={N4.noTexto} onElegir={() => { setPrueba('none'); }} />
            </div>
            <p className="rv-ca-nota rv-ca-nota--gris">
                <Icono nombre="shield" />
                <span>{TEXTOS.servicioSiempre}</span>
            </p>
        </div>
    );

    const trabajando = (
        <div className="rv-imp-trabajando" role="status">
            <Icono nombre="loader-circle" className="rv-gira" />
            <strong>{fase === 'importando' ? `Importando ${cifra(total)} clientes…` : TEXTOS.comprobando}</strong>
            <span>{fase === 'importando' ? TEXTOS.importandoTexto : TEXTOS.comprobandoTexto}</span>
        </div>
    );

    const tarjetas: [string, number, string, boolean, ResultadoLinea | null][] = (
        [
            ['Se importan', total, 'clientes en total', true, null],
            ['Nuevos', resumen.new ?? 0, 'Se crean', false, null],
            ['Actualizados', resumen.updated ?? 0, 'Se completan sus datos vacíos', false, null],
            ['Unidos', resumen.merged ?? 0, 'Mismo teléfono en el archivo', false, 'merged'],
            ['Dudosos', resumen.doubtful ?? 0, 'Quedan como sugerencia en Duplicados', false, 'doubtful'],
            ['Con errores', resumen.errors ?? 0, 'No se importan', false, 'error'],
            ['Suprimidos', resumen.suppressed ?? 0, 'Pidieron borrar sus datos: no se importan', false, 'suppressed'],
        ] as [string, number, string, boolean, ResultadoLinea | null][]
    ).filter((t, i) => i < 6 || t[1] > 0);

    const revision = importacion === null ? null : (
        <div className="rv-ca-cuerpo rv-imp-paso">
            {fase === 'hecha' && (
                <div className="rv-imp-hecha">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>{`${cifra(total)} clientes importados`}</strong>
                        <span>{terminoEl(importacion.finished_at)}</span>
                    </span>
                </div>
            )}
            {!nueva && !migracion && (
                <div>
                    {[
                        ['Archivo', [importacion.file_name ?? '', kilobytes(importacion.file_size)].filter((t) => t !== '').join(' · ')],
                        ['Lo subió', importacion.created_by_name ?? ''],
                        ['Cuándo', diaYHora(importacion.created_at)],
                    ].map(([k, v]) => (
                        <div key={k} className="rv-ca-dato">
                            <span>{k}</span>
                            <strong>{v}</strong>
                        </div>
                    ))}
                </div>
            )}
            <div className={`rv-imp-tarjetas${movil ? ' rv-imp-tarjetas--movil' : ''}`}>
                {tarjetas.map(([etiqueta, n, linea, oscura, ir]) => {
                    const ver = ir !== null && n > 0 && filtros.some((f) => f.resultado === ir);
                    const contenido = (
                        <>
                            <span className="rv-imp-tarjeta__cab">
                                <span>{etiqueta}</span>
                                {ver && (
                                    <span className="rv-imp-tarjeta__ver">
                                        Ver <Icono nombre="chevron-right" />
                                    </span>
                                )}
                            </span>
                            <strong>{cifra(n)}</strong>
                            <span>{linea}</span>
                        </>
                    );
                    return ver ? (
                        <button key={etiqueta} type="button" className="rv-imp-tarjeta" aria-label={`${etiqueta}: ${String(n)}. Ver sus líneas`} onClick={() => { setFiltro(ir); }}>
                            {contenido}
                        </button>
                    ) : (
                        <div key={etiqueta} className={`rv-imp-tarjeta${oscura ? ' rv-imp-tarjeta--oscura' : ''}`}>
                            {contenido}
                        </div>
                    );
                })}
            </div>
            <div className="rv-imp-permiso">
                <Icono nombre={declarada ? 'shield-check' : 'shield'} />
                <span className="rv-ca-fila__texto">
                    <strong>{declarada ? TEXTOS.conPrueba : TEXTOS.soloServicio}</strong>
                    <span>{declarada ? TEXTOS.conPruebaTexto : TEXTOS.soloServicioTexto}</span>
                    {nueva && fase === 'revisar' && puedeImportar && <small>{TEXTOS.cambiarNota}</small>}
                </span>
                {nueva && fase === 'revisar' && puedeImportar && (
                    <Boton
                        tipo="secundario"
                        icono="pencil"
                        onClick={() => {
                            setCambiando(true);
                            setPaso(1);
                        }}
                    >
                        Cambiar
                    </Boton>
                )}
            </div>
            {fase === 'hecha' && (resumen.doubtful ?? 0) > 0 && (
                <div className="rv-imp-botones">
                    <Boton tipo="secundario" icono="git-compare" onClick={() => { onIr('/clientes?apartado=duplicados'); }}>
                        Revisar duplicados
                    </Boton>
                    <Boton tipo="secundario" icono="users" onClick={() => { onIr('/clientes'); }}>
                        Ver clientes
                    </Boton>
                </div>
            )}
            <div className="rv-ca-bloque">
                <strong className="rv-imp-lineas-titulo">{TEXTOS.lineasTitulo}</strong>
                <Filtros
                    etiqueta="Filtrar líneas"
                    items={filtros}
                    valor={Math.max(0, filtros.findIndex((f) => f.resultado === filtroActual?.resultado))}
                    onCambio={(i) => {
                        const f = filtros[i];
                        if (f !== undefined) {
                            setFiltro(f.resultado);
                        }
                    }}
                />
                {lineasVistas === null ? (
                    <span className="rv-ca-nota rv-ca-nota--gris">Cargando líneas…</span>
                ) : lineasVistas.length === 0 ? (
                    <span className="rv-ca-nota rv-ca-nota--gris">{TEXTOS.sinLineas}</span>
                ) : (
                    <div role="list" aria-label="Líneas">
                        {lineasVistas.map((l) => {
                            const { campo, texto } = textoDeLinea(l);
                            const f = FILTROS_LINEAS.find((x) => x.resultado === l.outcome);
                            return (
                                <div key={`${String(l.line_number)}-${l.outcome}`} role="listitem" className={`rv-imp-linea rv-imp-linea--${l.outcome}`}>
                                    <span className="rv-imp-linea__baldosa">
                                        <Icono nombre={f?.icono ?? 'circle-alert'} />
                                    </span>
                                    <span className="rv-ca-fila__texto">
                                        <span className="rv-imp-linea__cab">
                                            <strong>{`Línea ${String(l.line_number)}`}</strong>
                                            {campo !== '' && <span>{` · ${campo}`}</span>}
                                        </span>
                                        <span className="rv-imp-linea__texto">{texto}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
                {filtroActual !== undefined && <p className="rv-ca-nota rv-ca-nota--gris">{filtroActual.resultado === 'error' && fase === 'hecha' ? NOTA_ERRORES_HECHA : filtroActual.nota}</p>}
            </div>
            {fase === 'revisar' && puedeImportar && (
                <div className="rv-imp-descartar">
                    <Boton tipo="secundario" rojo icono="x" onClick={() => { setDescartar(true); }}>
                        Descartar la vista previa
                    </Boton>
                </div>
            )}
        </div>
    );

    const noValido = (
        <div className="rv-ca-cuerpo rv-imp-paso">
            <div className="rv-ca-caja rv-ca-caja--error" role="alert">
                <span className="rv-ca-caja__fila">
                    <Icono nombre="circle-alert" />
                    <span>
                        <strong>{TEXTOS.noValido}</strong>
                        <br />
                        {(lineasVistas ?? []).map((l) => textoDeLinea(l).texto).filter((t) => t !== '').join(' ') || TEXTOS.noValidoTexto}
                    </span>
                </span>
            </div>
            {nueva && puedeImportar && (
                <div className="rv-imp-botones">
                    <Boton
                        icono="file-spreadsheet"
                        onClick={() => {
                            setImportacion(null);
                            setArchivo(null);
                            setPaso(0);
                        }}
                    >
                        Elegir otro archivo
                    </Boton>
                    <Boton tipo="secundario" icono="download" onClick={onPlantilla}>
                        Descargar plantilla
                    </Boton>
                </div>
            )}
        </div>
    );

    const cuerpo = (() => {
        switch (fase) {
            case 'editar':
            case 'subiendo':
                return paso === 0 ? pasoArchivo : pasoPermisos;
            case 'comprobando':
            case 'importando':
                return trabajando;
            case 'no-valido':
                return noValido;
            case 'descartada':
                return (
                    <p className="rv-ca-nota">
                        <Icono nombre="info" />
                        <span>{TEXTOS.descartada}</span>
                    </p>
                );
            case 'fallida':
                return (
                    <p className="rv-ca-nota">
                        <Icono nombre="triangle-alert" />
                        <span>{TEXTOS.haFallado}</span>
                    </p>
                );
            default:
                return revision;
        }
    })();

    const terminar = `Importar ${cifra(total)}${movil ? '' : ' clientes'}`;

    return (
        <>
            <PanelLateral abierto={apertura !== null} onCerrar={onCerrar} movil={movil} tamano="ficha" titulo={titulo} eyebrow={migracion ? 'Migración desde la v1' : 'Importación'} subtitulo={subtitulo} icono={nueva ? 'upload' : migracion ? 'history' : 'file-spreadsheet'}>
                <div className="rv-imp-panel">
                    <div className="rv-imp-panel__cab">
                        {nueva && fase !== 'hecha' ? <p className="rv-ca-ceja">{`Paso ${String(fase === 'editar' || fase === 'subiendo' ? paso + 1 : 3)} de 3 · ${PASOS[fase === 'editar' || fase === 'subiendo' ? paso : 2] ?? ''}`}</p> : <span />}
                        <Ayuda tarjetas={ayuda} />
                    </div>
                    {apertura !== null && cuerpo}
                    {apertura !== null && enPasos && (
                        <div className="rv-imp-pie">
                            <Pasos
                                total={3}
                                valor={fase === 'editar' || fase === 'subiendo' ? paso : 2}
                                movil={movil}
                                siguiente={paso === 1 ? (cambiando ? (movil ? 'Subir otra vez' : 'Subir otra vez y revisar') : 'Subir y revisar') : 'Siguiente'}
                                terminar={terminar}
                                icono="upload"
                                inactivo={paso === 0 && archivo === null && fase === 'editar'}
                                onInactivo={() => { setErrorArchivo(TEXTOS.faltaArchivo); }}
                                ocupado={fase === 'subiendo' || fase === 'comprobando' || ocupado}
                                ocupadoTexto={ocupado ? 'Importando…' : 'Subiendo…'}
                                sinVolver={fase === 'revisar'}
                                onCambio={(n) => {
                                    if (n === 2 && paso === 1) {
                                        subir();
                                        return;
                                    }
                                    if (n < 0) {
                                        return;
                                    }
                                    setPaso(n);
                                }}
                                onFin={confirmar}
                            />
                        </div>
                    )}
                    {apertura !== null && !nueva && fase === 'revisar' && puedeImportar && (
                        <div className="rv-imp-pie rv-imp-pie--derecha">
                            <Boton icono="upload" movil={movil} ocupado={ocupado} ocupadoTexto="Importando…" onClick={confirmar}>
                                {terminar}
                            </Boton>
                        </div>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={descartar}
                titulo={TEXTOS.descartarTitulo}
                texto={TEXTOS.descartarTexto}
                accion="Descartar"
                accionIcono="x"
                ocupado={ocupado}
                ocupadoTexto="Descartando…"
                movil={movil}
                onAccion={hacerDescartar}
                onCerrar={() => { setDescartar(false); }}
            />
        </>
    );
}
